import { ArrowLeft, Trash2, Loader2, Copy, Hash, Check, AlertCircle, CheckCircle2, ClipboardCheck, Download, Upload, Bot } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { SiteHeader } from '@/components/site-header';

import { useNavigate } from 'react-router-dom';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useAuth } from '@/context/AuthContext';
import { authenticatedClient } from '@/api/lib/client';
import { ASSISTANT_ENDPOINTS } from '@/api/assistants/assistants.utils';
import { useState, useEffect } from 'react';
import { AssistantActions } from './AssistantActions';
import { cn } from '@/lib/utils';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { InlineCallInterface } from '@/components/voice-call/InlineCallInterface';
import { downloadAssistantConfig, readJsonFile, processImportedConfig, validateImportedConfig } from '@/utils/assistantImportExport';
import type { Assistant } from '@/api/assistants/assistants.types';
import { VOICE_CALL_HIDDEN_ORG_IDS } from '@/config/restrictions';

// Max name length is 40 characters as per backend requirement
const MAX_NAME_LENGTH = 40;
const CUSTOM_MODE_EMPTY_URL_PLACEHOLDER = '__CUSTOM_SOUND_URL_PENDING__';

interface AssistantHeaderProps {
    name: string;
    setName: (name: string) => void;
    nameError: string;
    setNameError: (error: string) => void;
    onBack: () => void;

    assistantId?: string;
    onSave: () => void;
    isLoading?: boolean;
    isDuplicating?: boolean;
    onDuplicate?: () => void;
    backgroundSound?: string;
    hasUnsavedChanges?: boolean;
    config?: Omit<Assistant, 'name'>;
    onConfigUpdate?: (config: Omit<Assistant, 'name'>, name: string) => void;
}


export function AssistantHeader({
    name,
    setName,
    nameError,
    setNameError,
    onBack,

    assistantId,
    onSave,
    isLoading,
    isDuplicating,
    onDuplicate,
    backgroundSound,
    hasUnsavedChanges = false,
    config,
    onConfigUpdate,
}: AssistantHeaderProps) {
    const navigate = useNavigate();
    const { success, error } = useEnhancedToast();
    const { user } = useAuth();
    const [isDeleting, setIsDeleting] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const hasEditorAccess = user?.permissions?.assistant === 'EDITOR';
    
    // Initialize voice call with JWT authentication
    const voiceCall = useVoiceCall({
        // No publicKey needed - using JWT authentication
    });

    const handleDelete = async () => {
        if (!assistantId) return;

        setIsDeleting(true);
        try {
            await authenticatedClient.delete(ASSISTANT_ENDPOINTS.DELETE(assistantId));
            success({
                icon: CheckCircle2,
                title: "Assistant Deleted",
                description: "The assistant has been permanently removed",
                duration: 3000
            });
            setDialogOpen(false);
            navigate('/dashboard/assistants');
        } catch (err: unknown) {
            error({
                icon: AlertCircle,
                title: "Deletion Failed",
                description: err instanceof Error ? err.message : "Failed to delete assistant. Please try again.",
                duration: 4000
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSaveWithValidation = () => {
        if (backgroundSound === CUSTOM_MODE_EMPTY_URL_PLACEHOLDER) {
            error({
                icon: AlertCircle,
                title: "Audio URL Required",
                description: "Please set a URL for the custom background sound or choose a different option.",
                duration: 4000
            });
            return;
        }
        onSave();
    };

    const handleTalkToAssistant = () => {
        if (!assistantId) {
            error({
                icon: AlertCircle,
                title: "Assistant Not Saved",
                description: "Please save your assistant before starting a conversation",
                duration: 4000
            });
            return;
        }

        // Check for any unsaved changes in the assistant configuration
        if (hasUnsavedChanges) {
            setUnsavedChangesDialog(true);
        } else {
            startVoiceCall();
        }
    };

    const startVoiceCall = () => {
        if (!assistantId) return;
        voiceCall.startCall(assistantId);
    };

    const handleSaveAndCall = async () => {
        setUnsavedChangesDialog(false);
        await handleSaveWithValidation();
        // Wait a bit for save to complete
        setTimeout(() => {
            if (assistantId) {
                startVoiceCall();
            }
        }, 500);
    };

    const handleExport = async () => {
        if (!assistantId || !config) {
            error({
                icon: AlertCircle,
                title: "Export Failed",
                description: "Cannot export assistant configuration. Assistant data is not available.",
                duration: 4000
            });
            return;
        }

        setIsExporting(true);
        try {
            // Create a complete assistant object for export
            const assistantForExport: Assistant = {
                name: name,
                ...config,
                id: assistantId,
                _id: assistantId,
                orgId: '',
                organization_id: '',
                createdAt: '',
                updatedAt: '',
                created_at: '',
                updated_at: '',
                isServerUrlSecretSet: false
            };

            const filename = downloadAssistantConfig(assistantForExport);
            success({
                icon: Download,
                title: "Configuration Exported",
                description: `Assistant configuration saved as ${filename}`,
                duration: 4000
            });
        } catch (err) {
            error({
                icon: AlertCircle,
                title: "Export Failed",
                description: err instanceof Error ? err.message : "Failed to export configuration",
                duration: 4000
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            setIsImporting(true);
            try {
                const importedData = await readJsonFile(file);
                const validation = validateImportedConfig(importedData);

                // Show warnings if any
                if (validation.warnings.length > 0) {
                    validation.warnings.forEach(warning => {
                        success({
                            icon: AlertCircle,
                            title: "Import Warning",
                            description: warning,
                            duration: 3000
                        });
                    });
                }

                if (!validation.isValid) {
                    error({
                        icon: AlertCircle,
                        title: "Import Failed",
                        description: validation.errors.join('\n'),
                        duration: 6000
                    });
                    return;
                }

                // Process the configuration
                const processedConfig = processImportedConfig(importedData);
                const importedName = importedData.name || 'Imported Assistant';

                // Update the configuration
                if (onConfigUpdate) {
                    onConfigUpdate(processedConfig, importedName);
                }

                success({
                    icon: Upload,
                    title: "Configuration Imported",
                    description: `Successfully imported configuration for "${importedName}"`,
                    duration: 4000
                });

            } catch (err) {
                error({
                    icon: AlertCircle,
                    title: "Import Failed",
                    description: err instanceof Error ? err.message : "Failed to import configuration",
                    duration: 4000
                });
            } finally {
                setIsImporting(false);
            }
        };
        input.click();
    };

    return (
        <>
            <SiteHeader
                title={assistantId ? 'Edit Assistant' : 'New Assistant'}
                subtitle={assistantId ? 'Update your AI assistant configuration and settings' : 'Create a new AI voice assistant with custom configuration'}
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                <div className="flex items-center gap-2">
                    {/* Import/Export Button Group - Related data transfer actions */}
                    {hasEditorAccess && (assistantId ? (
                        <ButtonGroup>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleImport}
                                disabled={isImporting}
                                className="gap-2"
                            >
                                {isImporting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Upload className="h-4 w-4" />
                                )}
                                {isImporting ? 'Importing' : 'Import'}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleExport}
                                disabled={isExporting || !config}
                                className="gap-2"
                            >
                                {isExporting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4" />
                                )}
                                {isExporting ? 'Exporting' : 'Export'}
                            </Button>
                        </ButtonGroup>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleImport}
                            disabled={isImporting}
                            className="gap-2"
                        >
                            {isImporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4" />
                            )}
                            {isImporting ? 'Importing' : 'Import'}
                        </Button>
                    ))}

                    {assistantId && (
                        hasEditorAccess ? (
                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive gap-2">
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="gap-3">
                                    <DialogHeader className="space-y-1">
                                        <DialogTitle className="text-base">Delete Assistant</DialogTitle>
                                        <DialogDescription>
                                            Are you sure you want to permanently delete this assistant? This action cannot be undone.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDialogOpen(false)}
                                            disabled={isDeleting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={handleDelete}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Deleting
                                                </>
                                            ) : (
                                                'Delete'
                                            )}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        ) : (
                            <Button
                                variant="outline"
                                size="icon"
                                className="text-muted-foreground"
                                disabled
                                title="You need editor access to delete assistants"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )
                    )}
                    {assistantId && !VOICE_CALL_HIDDEN_ORG_IDS.includes(user?.organization_id ?? '') && (
                        <InlineCallInterface
                            isConnecting={voiceCall.isConnecting}
                            isConnected={voiceCall.isConnected}
                            isMuted={voiceCall.isMuted}
                            volumeLevel={voiceCall.volumeLevel}
                            isAssistantSpeaking={voiceCall.isAssistantSpeaking}
                            onStartCall={handleTalkToAssistant}
                            onEndCall={voiceCall.endCall}
                            onToggleMute={voiceCall.toggleMute}
                        />
                    )}
                    <AssistantActions
                        onSave={handleSaveWithValidation}
                        assistantId={assistantId}
                        isLoading={isLoading}
                        isDuplicating={isDuplicating}
                        onDuplicate={onDuplicate}
                    />
                </div>
            </SiteHeader>

            {/* Unsaved Changes Dialog */}
            <Dialog open={unsavedChangesDialog} onOpenChange={setUnsavedChangesDialog}>
                <DialogContent className="gap-3">
                    <DialogHeader className="space-y-1">
                        <DialogTitle className="text-base">Unsaved Changes</DialogTitle>
                        <DialogDescription>
                            You have unsaved changes. Save them before starting the conversation?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setUnsavedChangesDialog(false);
                                startVoiceCall();
                            }}
                        >
                            Start Without Saving
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSaveAndCall}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving
                                </>
                            ) : (
                                'Save and Start'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}